/* eslint-disable react-refresh/only-export-components -- React error boundaries require a class component. */
import { Alert, Button, Stack } from '@mui/material';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

function ErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();

  return (
    <Alert
      severity="error"
      action={
        <Stack direction="row" spacing={1}>
          <Button color="inherit" size="small" onClick={onRetry}>
            {t('actions.retry')}
          </Button>
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            {t('actions.reload')}
          </Button>
        </Stack>
      }
    >
      {t('errors.renderFailed')}
    </Alert>
  );
}

// React exposes render error boundaries only through class lifecycle methods; the fallback stays
// separate so it can use translation hooks.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Render failures must remain observable in production even before a remote reporter exists.
    console.error('Unhandled render error', error, errorInfo);
  }

  private readonly retry = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    return this.state.hasError ? <ErrorFallback onRetry={this.retry} /> : this.props.children;
  }
}
